/**
 * <admira-request-form> — the three request forms of plan §7.3, §7.4 and §7.9:
 * the B2B quote, the SEAP publication request and the TBI financing request.
 *
 * All three are Shopify `contact` forms, so the store receives them as email
 * with no app. What this element adds is the part a plain contact form lacks:
 *
 *   1. **Validation with reasons.** Each invalid field gets its own message,
 *      linked by `aria-describedby`, and `aria-invalid`. Focus goes to the first
 *      one. Messages are Romanian locale strings passed in as data attributes —
 *      no wording lives in this file (CC-01).
 *   2. **The Romanian fiscal code checksum** (`data-validate="cui"`). A CUI/CIF
 *      mistyped by one digit is otherwise only discovered when an invoice or a
 *      SEAP listing bounces, days later.
 *   3. **"Pick at least one" groups.** A `<fieldset data-require-one>` of
 *      checkboxes is invalid until one is ticked (`data-error-group`).
 *   4. **Submission that survives failure.** The form posts with `fetch`, and
 *      the outcome is read from the page Shopify returns — the server-rendered
 *      `[data-request-form-result]` marker, which is `form.posted_successfully?`
 *      and `form.errors`, not a sniffed query string (SPEC-migration-keystone
 *      §1.5.2). A network failure keeps everything typed and says so. A spam
 *      challenge (`/challenge`) cannot be completed inside `fetch`, so the form
 *      falls back to a normal submission and Shopify's own challenge page.
 *   5. **Shopify's hCaptcha goes first when it is on.** With spam protection
 *      on, Shopify binds every contact form (`data-hcaptcha-bound`): its own
 *      submit listener fetches a token and posts the form natively. A `fetch`
 *      of ours would leave without the token ("Missing CAPTCHA token", 400).
 *      So the listener here runs in the capture phase on this element — before
 *      Shopify's, which sits on the form — and stops an invalid form there;
 *      a valid, bound form is left to Shopify, and the reloaded page shows the
 *      server marker as it does without scripting.
 *
 * With scripting off, the form is an ordinary contact form that posts and
 * reloads, and the same server marker shows the outcome.
 */

const CUI_KEY = [7, 5, 3, 2, 1, 7, 5, 3, 2];

/**
 * Romanian CUI/CIF control digit, the ANAF algorithm: weights 753217532 over
 * the body padded to nine digits, sum × 10 mod 11, and 10 becomes 0. An `RO`
 * prefix (VAT payers) and spaces are accepted. 14399840 is a known-valid code.
 * @param {string} raw
 * @returns {boolean}
 */
export function isValidCui(raw) {
  const digits = String(raw).trim().toUpperCase().replace(/^RO/, '').replace(/\s+/g, '');
  if (!/^\d{2,10}$/.test(digits)) return false;

  const control = Number(digits.at(-1));
  const body = digits.slice(0, -1).padStart(9, '0');
  let sum = 0;
  for (let i = 0; i < 9; i++) sum += Number(body[i]) * CUI_KEY[i];

  const expected = ((sum * 10) % 11) % 10;
  return expected === control;
}

const VALIDATORS = {
  cui: (value) => isValidCui(value),
  phone: (value) => /^\+?[0-9 ().-]{9,20}$/.test(value) && value.replace(/\D/g, '').length >= 9,
  cpv: (value) => /^\d{8}-\d$/.test(value),
};

class AdmiraRequestForm extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  #form = null;

  connectedCallback() {
    this.#form = this.querySelector('form');
    if (!this.#form) return;

    this.#form.noValidate = true;
    this.addEventListener('submit', this.#onSubmit, { capture: true });
    this.#form.addEventListener('input', this.#onInput);
    this.#form.addEventListener('change', this.#onInput);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit, { capture: true });
    this.#form?.removeEventListener('input', this.#onInput);
    this.#form?.removeEventListener('change', this.#onInput);
  }

  /** Clears a field's error once it becomes valid — never adds one while typing. */
  #onInput = (event) => {
    const field = event.target;
    if (!(field instanceof HTMLElement)) return;

    // A "pick at least one" group carries its error on its first box, so a tick
    // anywhere in the group is what clears it.
    const group = field.closest('fieldset[data-require-one]');
    if (group instanceof HTMLFieldSetElement) {
      const first = group.querySelector('input[type="checkbox"]');
      if (first instanceof HTMLElement && first.getAttribute('aria-invalid') === 'true' && !this.#groupMessage(group)) {
        this.#setError(first, '');
      }
      return;
    }

    if (field.getAttribute('aria-invalid') !== 'true') return;
    if (!this.#messageFor(field)) this.#setError(field, '');
  };

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    const form = this.#form;
    if (!form || event.target !== form || form.dataset.nativeSubmit === 'true') return;

    const invalid = this.#validate();
    if (invalid.length) {
      // Stopped here, so Shopify's captcha listener never posts it.
      event.preventDefault();
      event.stopPropagation();
      this.#status(this.dataset.errorSummary ?? '', 'error');
      invalid[0].focus();
      return;
    }

    // A file still uploading (`<admira-file-upload>`) would leave its link out
    // of the request: hold the submit until it settles, then go again.
    if (form.querySelector('[data-upload-pending]')) {
      event.preventDefault();
      event.stopPropagation();
      this.#status(this.dataset.uploadWait ?? '', 'idle');
      await this.#waitFor(() => !form.querySelector('[data-upload-pending]'), 60000);
      this.#status('', 'idle');
      form.requestSubmit(event.submitter instanceof HTMLButtonElement ? event.submitter : null);
      return;
    }

    const submit = form.querySelector('[type="submit"]');
    // A button with an icon names its text span, so the icon survives the swap.
    const labelTarget = submit?.querySelector('[data-submit-label]') ?? submit;
    const label = labelTarget?.textContent ?? '';
    const restore = () => {
      if (!(submit instanceof HTMLButtonElement) || !this.isConnected) return;
      submit.disabled = false;
      submit.removeAttribute('aria-busy');
      if (labelTarget) labelTarget.textContent = label;
    };
    if (submit instanceof HTMLButtonElement) {
      submit.disabled = true;
      submit.setAttribute('aria-busy', 'true');
      if (this.dataset.sending && labelTarget) labelTarget.textContent = this.dataset.sending;
    }
    this.#status('', 'idle');

    if (form.hasAttribute('data-hcaptcha-bound')) {
      // Shopify's listener takes it from here and the page reloads. If the
      // captcha never answers, give the button back rather than hang.
      window.setTimeout(restore, 15000);
      return;
    }

    event.preventDefault();

    // Shopify binds a form on its first focus (`data-cptcha`) and finishes once
    // the captcha script loads. A submit in between waits for that, then goes
    // again — through the branch above.
    if (form.hasAttribute('data-cptcha')) {
      event.stopPropagation();
      const bound = await this.#waitFor(() => form.hasAttribute('data-hcaptcha-bound'), 10000);
      restore();
      if (bound) form.requestSubmit(submit instanceof HTMLButtonElement ? submit : null);
      else this.#status(this.dataset.errorNetwork ?? '', 'error');
      return;
    }

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        body: new FormData(form),
        headers: { Accept: 'text/html' },
      });

      if (response.url.includes('/challenge')) {
        // hCaptcha has to run on Shopify's page. Let the browser take it there.
        form.dataset.nativeSubmit = 'true';
        HTMLFormElement.prototype.submit.call(form);
        return;
      }

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = this.#readResult(await response.text());
      if (result.state === 'success') {
        this.#succeed(result.message);
        return;
      }
      this.#status(result.message || this.dataset.errorNetwork || '', 'error');
    } catch (error) {
      console.error('admira-request-form:', error);
      this.#status(this.dataset.errorNetwork ?? '', 'error');
    } finally {
      restore();
    }
  };

  /**
   * @param {() => boolean} done
   * @param {number} limit - milliseconds
   * @returns {Promise<boolean>} whether `done` became true within the limit
   */
  async #waitFor(done, limit) {
    for (let waited = 0; waited < limit; waited += 100) {
      if (done()) return true;
      await new Promise((resolve) => setTimeout(resolve, 100));
    }
    return done();
  }

  /** @returns {HTMLElement[]} the invalid fields, in document order */
  #validate() {
    const form = this.#form;
    if (!form) return [];

    const invalid = [];
    for (const field of form.elements) {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) continue;
      if (field.disabled || field.type === 'hidden' || field.type === 'file' || field.closest('fieldset:disabled')) continue;

      const message = this.#messageFor(field);
      this.#setError(field, message);
      if (message) invalid.push(field);
    }

    // `required` cannot express "at least one of these boxes", so a fieldset
    // marked `data-require-one` is checked as a whole. The error goes on its
    // first box (focusable, and the slot lands at the end of the fieldset).
    for (const group of form.querySelectorAll('fieldset[data-require-one]')) {
      if (!(group instanceof HTMLFieldSetElement) || group.disabled) continue;
      const first = group.querySelector('input[type="checkbox"]');
      if (!(first instanceof HTMLInputElement)) continue;

      const message = this.#groupMessage(group);
      this.#setError(first, message);
      if (message) invalid.push(first);
    }

    return invalid.sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));
  }

  /**
   * @param {HTMLFieldSetElement} group
   * @returns {string} the group's error, or '' when a box is ticked
   */
  #groupMessage(group) {
    if (group.querySelector('input[type="checkbox"]:checked')) return '';
    return group.dataset.errorGroup ?? this.dataset.errorGroup ?? '';
  }

  /**
   * @param {HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement} field
   * @returns {string} the error, or '' when valid
   */
  #messageFor(field) {
    const value = field.value.trim();
    const messages = this.dataset;

    if (field instanceof HTMLInputElement && field.type === 'checkbox') {
      return field.required && !field.checked ? messages.errorConsent ?? '' : '';
    }
    if (field.required && value === '') return messages.errorRequired ?? '';
    if (value === '') return '';

    if (field instanceof HTMLInputElement) {
      if (field.type === 'email' && field.validity.typeMismatch) return messages.errorEmail ?? '';
      if (field.type === 'number' && (field.validity.rangeUnderflow || field.validity.rangeOverflow || field.validity.badInput)) {
        return field.dataset.errorRange ?? messages.errorRange ?? '';
      }
    }

    const rule = field.dataset.validate;
    if (rule && rule in VALIDATORS && !VALIDATORS[/** @type {keyof VALIDATORS} */ (rule)](value)) {
      return field.dataset.errorPattern ?? '';
    }
    return '';
  }

  /**
   * @param {HTMLElement} field
   * @param {string} message
   */
  #setError(field, message) {
    const id = `${field.id}-error`;
    let slot = /** @type {HTMLElement | null} */ (this.querySelector(`[id="${id}"]`));

    if (!message) {
      field.removeAttribute('aria-invalid');
      if (slot) slot.textContent = '';
      return;
    }

    if (!slot) {
      slot = document.createElement('p');
      slot.id = id;
      slot.className = 'request-form__field-error';
      (field.closest('[data-field]') ?? field.parentElement)?.append(slot);
      const describedBy = new Set((field.getAttribute('aria-describedby') ?? '').split(' ').filter(Boolean));
      describedBy.add(id);
      field.setAttribute('aria-describedby', [...describedBy].join(' '));
    }
    slot.textContent = message;
    field.setAttribute('aria-invalid', 'true');
  }

  /**
   * Finds this form's server marker in the page Shopify returned. The form may
   * sit inside a `<template>` there (the B2B dialog builds on open), so the
   * templates are searched too.
   * @param {string} html
   * @returns {{ state: string, message: string }}
   */
  #readResult(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const key = this.dataset.formKey;
    const selector = `[data-request-form-result][data-form-key="${key}"]`;

    let marker = doc.querySelector(selector);
    if (!marker) {
      for (const template of doc.querySelectorAll('template')) {
        marker = template.content.querySelector(selector);
        if (marker) break;
      }
    }

    return {
      state: marker?.getAttribute('data-request-form-result') ?? 'unknown',
      message: marker?.textContent?.trim() ?? '',
    };
  }

  /** @param {string} message */
  #succeed(message) {
    const form = this.#form;
    if (!form) return;

    const note = document.createElement('p');
    note.className = 'request-form__success';
    note.setAttribute('role', 'status');
    note.tabIndex = -1;
    note.textContent = message || this.dataset.success || '';
    form.replaceWith(note);
    note.focus();

    this.dispatchEvent(new CustomEvent('request-form:success', { bubbles: true }));
  }

  /**
   * @param {string} message
   * @param {'idle' | 'error'} state
   */
  #status(message, state) {
    const region = this.querySelector('[data-form-status]');
    if (!(region instanceof HTMLElement)) return;
    region.textContent = message;
    region.dataset.state = state;
  }
}

if (!customElements.get('admira-request-form')) {
  customElements.define('admira-request-form', AdmiraRequestForm);
}
